import { z } from 'zod';
/**
 * The in-app notification CATALOGUE.
 *
 * Like the feature-flag catalogue, the set of categories and types lives in
 * code and the database holds only the facts: a stored notification carries its
 * `type` as a plain string column and the category is derived from the type
 * here, never stored. That keeps a category move (say, reclassifying a type) a
 * one-line change with no backfill, and makes a type a union member so a typo
 * is a type error.
 *
 * Adding a type: add its key to `NOTIFICATION_TYPE_KEYS` and an entry to
 * `NOTIFICATION_TYPES`. No migration is needed.
 *
 * WARNING - these values are persisted. Type keys, category keys, channels,
 * skip reasons and failure codes are stored as plain strings in
 * `notifications`, `notification_deliveries` and `notification_preferences`
 * (they are validated here, not by the database, so the catalogue stays easy to
 * edit). Renaming or removing one of them in this file therefore leaves existing
 * rows holding the old value, so it needs a data migration of those rows in the
 * same change. Adding a value needs none.
 *
 * Removing a type: delete the entry AND delete its rows in a migration. A row
 * whose type is no longer in the catalogue cannot be mapped to a category, so
 * it would never appear in a category-filtered list.
 */
/**
 * PERSISTED as `category` in `notification_preferences`: renaming or removing a key needs a data migration.
 * The order is the display order in the user's notification settings: messages from Bill itself lead,
 * practice activity follows.
 */
export const NOTIFICATION_CATEGORY_KEYS = [
    'action-required',
    'service-notices',
    'product-news',
    'daily-bank-summary',
    'daily-practice-report',
];
export const NotificationCategoryKeySchema = z.enum(NOTIFICATION_CATEGORY_KEYS);
/** External channels a notification can also be delivered on; the in-app row always exists and is not a delivery. */
/** PERSISTED as `channel` on deliveries and preferences: renaming or removing a channel needs a data migration. */
export const NOTIFICATION_DELIVERY_CHANNELS = ['email', 'whatsapp'];
export const NotificationDeliveryChannelSchema = z.enum(NOTIFICATION_DELIVERY_CHANNELS);
/**
 * Everything a user can choose per category: whether it appears in Bill (the bell
 * and its unread count), and each delivery channel. `in-app` decides only what the
 * bell shows; the notification row is raised either way, since deliveries hang off it.
 * PERSISTED as `channel` in `notification_preferences`, like the delivery channels.
 */
export const NOTIFICATION_PREFERENCE_CHANNELS = ['in-app', ...NOTIFICATION_DELIVERY_CHANNELS];
export const NotificationPreferenceChannelSchema = z.enum(NOTIFICATION_PREFERENCE_CHANNELS);
/** Mirrors the `NotificationDeliveryStatus` Prisma enum; change both together, with a migration. */
export const NOTIFICATION_DELIVERY_STATUSES = ['PENDING', 'SENT', 'DELIVERED', 'FAILED', 'SKIPPED'];
/** PERSISTED as `skipReason` on deliveries: renaming or removing a reason needs a data migration. */
export const NOTIFICATION_DELIVERY_SKIP_REASONS = [
    'preference_off',
    'recipient_no_access',
    'provider_disabled',
    'recipient_no_verified_phone',
    // No longer recorded: the account lacked the rollout feature flag that gated external delivery. Kept for the rows that hold it.
    'notifications_not_enabled',
    // The user belongs to several accounts addressed by one announcement, and another account sends their email.
    'sent_via_other_account',
    // The sender chose to deliver the announcement in the app only, so no external delivery goes out.
    'in_app_only',
];
/** PERSISTED as `failureCode` on deliveries: renaming or removing a code needs a data migration. */
export const NOTIFICATION_DELIVERY_FAILURE_CODES = ['provider_rejected', 'bounced_hard', 'bounced_soft'];
export const NOTIFICATION_CATEGORIES = {
    'action-required': {
        key: 'action-required',
        label: 'Action required',
        description: 'Something needs you to act, such as reconnecting an inbox or fixing a failed payment.',
        // No WhatsApp yet: there is no WhatsApp sender, so offering it would only record deliveries that never go.
        // Both channels locked on: these are transactional (Bill has stopped doing something for the user until
        // they act), so neither the bell nor the email can be switched off, and the email carries no unsubscribe link.
        channels: {
            'in-app': { defaultEnabled: true, locked: true },
            email: { defaultEnabled: true, locked: true },
        },
    },
    'service-notices': {
        key: 'service-notices',
        label: 'Service notices',
        description: 'Important notices about the Bill service, such as planned maintenance or changes that affect you.',
        // Both channels locked on: a service notice must reach everyone it is sent to, so neither can be switched off.
        channels: {
            'in-app': { defaultEnabled: true, locked: true },
            email: { defaultEnabled: true, locked: true },
        },
    },
    'product-news': {
        key: 'product-news',
        label: 'Product news',
        description: 'New features and tips for getting more out of Bill.',
        // Not offered on WhatsApp: promotional messages there need a template and a separate opt-in.
        channels: {
            'in-app': { defaultEnabled: true, locked: true },
            email: { defaultEnabled: false, locked: false },
        },
    },
    // The summaries are opt-in: each is offered only to the users it can reach (see
    // `eligible` on the type's schedule), so neither is ever sent to someone who did not ask.
    // Email only: a summary is long-form, so a one-line bell item would only repeat the dashboard,
    // and WhatsApp offers no template for it.
    'daily-bank-summary': {
        key: 'daily-bank-summary',
        label: 'Daily bank summary',
        description: 'A morning email on days new bank transactions arrive: money in and out, balances and allocations waiting for you.',
        channels: { email: { defaultEnabled: false, locked: false } },
    },
    'daily-practice-report': {
        key: 'daily-practice-report',
        label: 'Daily practice report',
        description: 'A weekday morning email with the time captured in your practice and your unbilled work.',
        channels: { email: { defaultEnabled: false, locked: false } },
    },
};
/** The categories as a list, in a stable order for rendering. */
export const NOTIFICATION_CATEGORY_DEFINITIONS = NOTIFICATION_CATEGORY_KEYS.map((key) => NOTIFICATION_CATEGORIES[key]);
export const ISO_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];
/** No user time zone is stored yet, and every practice is in South Africa, which has no daylight saving. */
export const NOTIFICATION_DELIVERY_HOURS_TIME_ZONE = 'Africa/Johannesburg';
/** Office hours, applied to a user who has never set their own. */
export const DEFAULT_NOTIFICATION_DELIVERY_HOURS = {
    days: [1, 2, 3, 4, 5],
    startMinute: 8 * 60,
    endMinute: 17 * 60,
};
export const MINUTES_PER_DAY = 24 * 60;
/** True when the hours cover every minute of the week, i.e. deliveries are never held. */
export function isAnyTimeDeliveryHours(hours) {
    return hours.days.length === ISO_WEEKDAYS.length && hours.startMinute === 0 && hours.endMinute === MINUTES_PER_DAY;
}
/** PERSISTED as `type` on notifications: renaming or removing a key needs a data migration. */
export const NOTIFICATION_TYPE_KEYS = [
    'daily-practice-report',
    'daily-bank-summary',
    'product-announcement',
    'service-notice',
    'email-inbox-reconnect-required',
    'calendar-reconnect-required',
];
export const NotificationTypeKeySchema = z.enum(NOTIFICATION_TYPE_KEYS);
export const NOTIFICATION_TYPES = {
    'daily-practice-report': {
        key: 'daily-practice-report',
        category: 'daily-practice-report',
        label: 'Daily practice report',
        description: 'A weekday morning summary of the time captured in your practice, the entries others captured for you, and your unbilled work.',
    },
    'product-announcement': {
        key: 'product-announcement',
        category: 'product-news',
        label: 'Product announcement',
        description: 'News from the Bill team about new features and how to get more out of Bill.',
    },
    'service-notice': {
        key: 'service-notice',
        category: 'service-notices',
        label: 'Service notice',
        description: 'An important notice from the Bill team about the service, such as planned maintenance.',
    },
    'daily-bank-summary': {
        key: 'daily-bank-summary',
        category: 'daily-bank-summary',
        label: 'Daily bank summary',
        description: 'A morning summary of the money that moved in your bank accounts since the last one, and the allocations waiting for your approval. Sent only on days when new bank transactions arrived.',
    },
    'email-inbox-reconnect-required': {
        key: 'email-inbox-reconnect-required',
        category: 'action-required',
        label: 'Reconnect your inbox',
        description: 'An email inbox you connected has stopped syncing with Bill and needs reconnecting.',
    },
    'calendar-reconnect-required': {
        key: 'calendar-reconnect-required',
        category: 'action-required',
        label: 'Reconnect your calendar',
        description: 'A calendar you connected has stopped syncing with Bill and needs reconnecting.',
    },
};
/** The types as a list, in a stable order for rendering. */
export const NOTIFICATION_TYPE_DEFINITIONS = NOTIFICATION_TYPE_KEYS.map((key) => NOTIFICATION_TYPES[key]);
export function getNotificationCategory(type) {
    return NOTIFICATION_TYPES[type].category;
}
export function getNotificationTypesInCategory(category) {
    return NOTIFICATION_TYPE_KEYS.filter((key) => NOTIFICATION_TYPES[key].category === category);
}
/**
 * True for an in-app path such as `/matters/abc`, false for `//host`,
 * `javascript:`, a backslash or a control character. Browsers strip tab, LF and
 * CR while parsing a URL, so `/\t/host` would re-form a protocol-relative link
 * and leave the app. The server checks it on write and the bell checks it again
 * before rendering a link.
 */
export function isAppRelativePath(href) {
    if (!href.startsWith('/') || href.startsWith('//'))
        return false;
    return ![...href].some((ch) => {
        const code = ch.charCodeAt(0);
        return code <= 0x1f || code === 0x7f || ch === '\\';
    });
}
/**
 * True for a destination a notification may link to: an in-app path (see
 * `isAppRelativePath`) or an absolute `https:` URL. An `https:` URL must carry no
 * credentials, whitespace, backslash or control character, which a browser would
 * strip or reinterpret while parsing. The server checks it on write, and the bell
 * and the email check it again before rendering a link.
 */
export function isNotificationHref(href) {
    if (isAppRelativePath(href))
        return true;
    if (!href.startsWith('https://'))
        return false;
    if ([...href].some((ch) => ch.charCodeAt(0) <= 0x20 || ch.charCodeAt(0) === 0x7f || ch === '\\'))
        return false;
    if (!URL.canParse(href))
        return false;
    const url = new URL(href);
    return url.protocol === 'https:' && url.hostname !== '' && url.username === '' && url.password === '';
}
